import { CheckCircle2, CircleAlert, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../lib/api';
import { cn } from '../lib/cn';

/**
 * Transient feedback for something the person just did.
 *
 * Before this existed every screen invented its own: a green line under one
 * form, a badge that appeared beside a button on another, and on most nothing
 * at all — a save that worked looked exactly like a save that had not been
 * clicked. A toast is for confirming an action and for an error that has no
 * field to sit beside. Errors that belong to a field still go next to the
 * field; a toast saying "the email is invalid" with no pointer to which one is
 * worse than nothing.
 *
 * Announced through a live region, so a screen reader hears the outcome
 * without focus moving away from the control that caused it.
 */

type ToastTone = 'positive' | 'critical' | 'neutral';

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string | undefined;
}

interface ToastApi {
  /** An action worked. Dismisses itself. */
  success: (title: string, description?: string) => void;
  /**
   * Something failed. Accepts the error itself, so callers do not each
   * re-implement turning an `ApiError` into a sentence.
   */
  error: (titleOrError: unknown, description?: string) => void;
  info: (title: string, description?: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long each tone stays up. Errors stay longer: they need reading. */
const DURATION: Record<ToastTone, number> = {
  positive: 4000,
  neutral: 5000,
  critical: 9000,
};

/** More than this and older ones are dropped; a stack of ten helps nobody. */
const MAX_VISIBLE = 4;

/**
 * A person-readable account of a failure.
 *
 * The server's message is already written for people — it is what the API's
 * error envelope is for — so it is used as it stands. A request id is kept
 * because it is what turns "it broke" into something findable in the logs.
 * Anything that is not an `ApiError` is a bug in this application, and its
 * own message is not something to show anybody.
 */
export function describeError(error: unknown): { title: string; description?: string } {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return { title: "You don't have permission to do that." };
    }
    // A session that ended mid-task. The session layer also sends the person
    // to sign in; this is what they read in the moment before it does.
    if (error.status === 401 && error.code !== 'invalid_credentials') {
      return { title: 'Your session has ended. Sign in again to continue.' };
    }
    // A validation failure's top-level message is deliberately generic — the
    // server puts the substance in the field list — so with no field to show
    // it beside, the first field message is the useful sentence.
    const first = error.details?.[0];
    const message = error.code === 'validation_failed' && first ? first.message : error.message;
    return {
      title: message,
      ...(error.requestId ? { description: `Reference: ${error.requestId}` } : {}),
    };
  }
  return { title: 'Something went wrong. Please try again.' };
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((tone: ToastTone, title: string, description?: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, tone, title, description }].slice(-MAX_VISIBLE));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (title, description) => push('positive', title, description),
      info: (title, description) => push('neutral', title, description),
      error: (titleOrError, description) => {
        if (typeof titleOrError === 'string') {
          push('critical', titleOrError, description);
          return;
        }
        const described = describeError(titleOrError);
        push('critical', described.title, description ?? described.description);
      },
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider');
  return context;
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  return (
    <div
      // Two regions rather than one: an error interrupts, a confirmation waits
      // its turn. Putting both in an assertive region makes every save shout.
      className="pointer-events-none fixed bottom-4 end-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
    >
      <div aria-live="polite" className="flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.tone !== 'critical')
          .map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
          ))}
      </div>
      <div aria-live="assertive" className="flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.tone === 'critical')
          .map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
          ))}
      </div>
    </div>
  );
}

const TONE_STYLES: Record<ToastTone, { icon: typeof Info; accent: string }> = {
  positive: { icon: CheckCircle2, accent: 'text-positive' },
  critical: { icon: CircleAlert, accent: 'text-critical' },
  neutral: { icon: Info, accent: 'text-informative' },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  const { icon: Icon, accent } = TONE_STYLES[toast.tone];

  // Hovering holds it: somebody reading an error should not have it vanish
  // halfway through the sentence.
  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), DURATION[toast.tone]);
    return () => window.clearTimeout(timer);
  }, [paused, toast.id, toast.tone, onDismiss]);

  return (
    <div
      role={toast.tone === 'critical' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cn(
        'animate-rise pointer-events-auto flex items-start gap-2.5 rounded-card border border-line',
        'bg-raised px-3 py-2.5 shadow-float',
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', accent)} />
      <div className="min-w-0 flex-1">
        <p dir="auto" className="text-body font-medium text-ink">
          {toast.title}
        </p>
        {toast.description && (
          <p dir="auto" className="mt-0.5 text-mini break-words text-ink-muted">
            {toast.description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        className="-me-1 -mt-0.5 rounded p-1 text-ink-subtle transition-colors hover:bg-sunken hover:text-ink"
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </div>
  );
}
